import type { Vector3 } from 'three';
import { TICK_DT } from '../movement/constants';
import { type ZoneDef, zoneContains } from './zones';

export type RunState = 'idle' | 'running' | 'finished';

export interface Split {
  checkpoint: number;
  ticks: number;
}

/**
 * Deterministic run state machine, advanced once per simulation tick.
 * Time is counted in ticks (100 Hz) — wall clocks never touch a PB.
 */
export class RunTimer {
  state: RunState = 'idle';
  ticks = 0;
  /** False once a checkpoint was skipped — the run can finish but never records. */
  valid = true;
  readonly splits: Split[] = [];

  private readonly start: ZoneDef | undefined;
  private readonly checkpoints: ZoneDef[];
  private readonly ends: ZoneDef[];
  private nextCheckpoint = 0;

  constructor(zones: ZoneDef[]) {
    this.start = zones.find((z) => z.kind === 'start');
    this.checkpoints = zones.filter((z) => z.kind === 'checkpoint');
    this.ends = zones.filter((z) => z.kind === 'end');
  }

  /** Call once per simulation tick with the player position (feet). */
  tick(position: Vector3): void {
    if (this.state === 'finished') {
      return;
    }

    if (this.state === 'idle') {
      // Arm: the run starts the first tick the player is outside the start zone.
      if (this.start && !zoneContains(this.start, position)) {
        this.state = 'running';
      } else {
        return;
      }
    }

    this.ticks++;

    for (let i = this.nextCheckpoint; i < this.checkpoints.length; i++) {
      const checkpoint = this.checkpoints[i];
      if (checkpoint && zoneContains(checkpoint, position)) {
        if (i > this.nextCheckpoint) {
          this.valid = false; // skipped one or more checkpoints
        }
        this.splits.push({ checkpoint: i, ticks: this.ticks });
        this.nextCheckpoint = i + 1;
        break;
      }
    }

    if (this.ends.some((end) => zoneContains(end, position))) {
      if (this.nextCheckpoint < this.checkpoints.length) {
        this.valid = false;
      }
      this.state = 'finished';
    }
  }

  reset(): void {
    this.state = 'idle';
    this.ticks = 0;
    this.valid = true;
    this.splits.length = 0;
    this.nextCheckpoint = 0;
  }

  seconds(): number {
    return this.ticks * TICK_DT;
  }
}

/** mm:ss.cc — the only time format the game displays. */
export function formatTicks(ticks: number): string {
  const totalCs = Math.round(ticks * TICK_DT * 100);
  const minutes = Math.floor(totalCs / 6000);
  const seconds = Math.floor((totalCs % 6000) / 100);
  const centis = totalCs % 100;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(centis).padStart(2, '0')}`;
}
